/**
 * Cliente del puente hacia la extension de Chrome que hace el POST a la Raspberry.
 *
 * La pagina no puede llamar a la Pi directamente: se sirve por HTTPS y la Pi escucha HTTP
 * plano (mixed content), y ademas seria cross-origin a una IP privada (CORS). El totem
 * (Electron) esquiva lo mismo haciendo el POST desde el proceso main; aca el equivalente es
 * el service worker de la extension.
 *
 * Contrato de mensajes: extension/PROTOCOL.md (fuente de verdad de los `type` de abajo).
 */

/** El puerto de la Raspberry es SIEMPRE 3000. Solo ip y endpoint son dinamicos. */
export const RASPBERRY_PORT = 3000;

/**
 * Mayor que el timeout de la extension (8000 ms), para que sea ella la que corte y nosotros
 * solo cubramos el caso de que no conteste nunca. Ninguna promesa queda colgada.
 */
const BRIDGE_TIMEOUT_MS = 10000;

/** Subir junto con extension/manifest.json cuando un cambio de protocolo lo requiera. */
export const MIN_EXTENSION_VERSION = "1.0.0";

const MARKER_ATTR = "data-passapp-rb-extension";

export type RaspberryErrorCode =
  | "invalid_request"
  | "invalid_host"
  | "forbidden_origin"
  | "http_error"
  | "timeout"
  | "bridge_unavailable"
  | "extension_missing"
  /** TypeError: Failed to fetch. AMBIGUO: bloqueo de LNA o Pi apagada. Ver PROTOCOL.md. */
  | "unreachable"
  | "unknown";

export interface VectorizeResult {
  ok: boolean;
  status?: number;
  durationMs?: number;
  data?: unknown;
  url?: string;
  error?: string;
  errorCode?: RaspberryErrorCode;
  rawError?: { name: string | null; message: string };
}

export interface VectorizeRequest {
  ip: string;
  port?: number;
  path: string;
  token: string;
  imageData: string;
}

// --- Deteccion -------------------------------------------------------------------------
// Se hace por atributo en <html>, no por handshake: el content script corre en
// document_start, asi que la marca ya esta puesta cuando arranca React. Sincrono, sin race.

export function getRaspberryBridgeVersion(): string | null {
  if (typeof document === "undefined") return null;
  return document.documentElement.getAttribute(MARKER_ATTR);
}

export function isRaspberryBridgeAvailable(): boolean {
  return getRaspberryBridgeVersion() !== null;
}

/** Compara versiones tipo '1.2.3'. Devuelve <0, 0 o >0. */
function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** true solo si esta instalada Y es mas vieja que la minima. Ausente no cuenta como vieja. */
export function isRaspberryBridgeOutdated(): boolean {
  const version = getRaspberryBridgeVersion();
  if (!version) return false;
  return compareVersions(version, MIN_EXTENSION_VERSION) < 0;
}

// --- Transporte ------------------------------------------------------------------------

interface PendingRequest {
  resolve: (result: VectorizeResult) => void;
  timer: number;
}

/**
 * Un solo listener a nivel de modulo + Map por requestId, en vez de un listener por llamada.
 * Sin correlacion por id las respuestas serian ambiguas: un addEvent puede traer varios
 * targets, y con acompañantes hay varios addEvent en paralelo.
 */
const pending = new Map<string, PendingRequest>();
let listenerRegistered = false;

function settle(requestId: string, result: VectorizeResult): void {
  const entry = pending.get(requestId);
  if (!entry) return; // ya resuelto por timeout, o id desconocido
  pending.delete(requestId);
  window.clearTimeout(entry.timer);
  entry.resolve(result);
}

function ensureListener(): void {
  if (listenerRegistered || typeof window === "undefined") return;
  listenerRegistered = true;

  window.addEventListener("message", (event: MessageEvent) => {
    // Solo mensajes de esta misma pagina.
    if (event.source !== window) return;
    if (event.origin !== window.location.origin) return;

    const data = event.data;
    if (!data || typeof data !== "object") return;

    if (data.type === "RB_RESPONSE" && data.requestId) {
      settle(data.requestId, (data.result || { ok: false }) as VectorizeResult);
      return;
    }

    if (data.type === "RB_ERROR" && data.requestId) {
      settle(data.requestId, {
        ok: false,
        error: data.error || "Error del puente",
        errorCode: (data.errorCode as RaspberryErrorCode) || "unknown",
      });
    }
  });
}

function newRequestId(): string {
  // randomUUID necesita secure context; en prod (HTTPS) y en localhost esta. El fallback
  // cubre el resto sin agregar dependencias — solo necesita ser unico dentro de la pagina.
  const cryptoObj = window.crypto as Crypto | undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `rb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Manda una foto a una Raspberry a traves de la extension.
 *
 * Nunca rechaza: los fallos vuelven como `{ ok: false, errorCode }`. Quien llama la usa
 * fire-and-forget y no tiene que envolverla en try/catch.
 */
export function postToRaspberry(request: VectorizeRequest): Promise<VectorizeResult> {
  if (!isRaspberryBridgeAvailable()) {
    return Promise.resolve({
      ok: false,
      error: "La extension del puente Raspberry no esta instalada",
      errorCode: "extension_missing",
    });
  }

  ensureListener();

  const requestId = newRequestId();

  return new Promise<VectorizeResult>((resolve) => {
    const timer = window.setTimeout(() => {
      pending.delete(requestId);
      resolve({
        ok: false,
        error: `La extension no respondio en ${BRIDGE_TIMEOUT_MS}ms`,
        errorCode: "bridge_unavailable",
      });
    }, BRIDGE_TIMEOUT_MS);

    pending.set(requestId, { resolve, timer });

    window.postMessage(
      {
        type: "RB_FETCH",
        requestId,
        ip: request.ip,
        port: request.port ?? RASPBERRY_PORT,
        path: request.path,
        token: request.token,
        imageData: request.imageData,
      },
      window.location.origin
    );
  });
}
